import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";
import axios from "axios";
import SectionCard from "./SectionCard";

const post = (ep, f) => {
  const fd = new FormData();
  Object.entries(f).forEach(([k,v]) => { if(v!=null) fd.append(k,v); });
  return axios.post(`${API_BASE}${ep}`, fd).then(r=>r.data).catch(()=>null);
};

const AC = (v) => v>=30?"#16A34A":v>=10?"#D97706":"#DC2626";

function AvailRing({ value }) {
  const v = parseInt(value)||0, r=18, circ=2*Math.PI*r;
  const color = AC(v);
  return (
    <div style={{textAlign:"center"}}>
      <div style={{position:"relative",width:44,height:44}}>
        <svg width="44" height="44" style={{transform:"rotate(-90deg)"}}>
          <circle cx="22" cy="22" r={r} fill="none" style={{ stroke:"var(--fc-border-light)" }} strokeWidth="5"/>
          <circle cx="22" cy="22" r={r} fill="none" stroke={color} strokeWidth="5"
            strokeDasharray={`${Math.min(v/40,1)*circ} ${circ}`} strokeLinecap="round"/>
        </svg>
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color}}>{v}</div>
      </div>
      <div style={{fontSize:9,fontWeight:700,color,textTransform:"uppercase",marginTop:-2}}>{v>=30?"Avail":v>=10?"Part":"Busy"}</div>
    </div>
  );
}

function ScoreBreakdown({ item, candidateName }) {
  const [activeTab, setActiveTab] = useState("Skills");
  const enriched = item?.enriched_score_data;
  const profile  = item?.profile;

  if (!enriched) return (
    <div style={{padding:"12px 16px",background:"var(--fc-bg-light)",borderTop:"1px solid var(--fc-border-light)"}}>
      <div style={{fontSize:12,color:"var(--fc-text-subtle)",fontStyle:"italic"}}>No detailed score breakdown available.</div>
    </div>
  );

  const TABS = ["Skills","Certifications","Education","Industries","Languages"].filter(t => enriched[t]);
  const Chip = ({label,found}) => (
    <span style={{padding:"3px 9px",borderRadius:99,fontSize:11,fontWeight:600,background:found?"var(--fc-green-light)":"var(--fc-red-light)",color:found?"#16A34A":"#DC2626",border:`1px solid ${found?"var(--fc-green-border)":"var(--fc-red-border)"}`}}>{label}</span>
  );

  const data = enriched[activeTab];
  return (
    <div style={{padding:"14px 16px",background:"var(--fc-bg-light)",borderTop:"1px solid var(--fc-border-light)"}}>
      <div style={{fontSize:11,fontWeight:700,color:"var(--fc-text-subtle)",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>
        Score Breakdown — {profile?.r_name}
      </div>
      {TABS.length > 0 ? (
        <>
          <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
            {TABS.map(t => (
              <button key={t} onClick={()=>setActiveTab(t)}
                style={{padding:"4px 12px",borderRadius:99,border:`1px solid ${activeTab===t?"#2563EB":"var(--fc-border)"}`,background:activeTab===t?"#2563EB":"var(--fc-card-bg)",color:activeTab===t?"#fff":"var(--fc-text-muted)",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                {t}
              </button>
            ))}
          </div>
          {data && (
            <div>
              {data.Found?.length > 0 && (
                <div style={{marginBottom:10}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#16A34A",marginBottom:6}}>✓ Found in both profiles</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {data.Found.map((s,i) => <Chip key={i} label={s.Skill||s} found={true} />)}
                  </div>
                </div>
              )}
              {data.NotFound?.length > 0 && (
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:"#DC2626",marginBottom:6}}>
                    ✗ Not found in <strong>{profile?.r_name}</strong>'s profile
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {data.NotFound.map((s,i) => <Chip key={i} label={s.Skill||s} found={false} />)}
                  </div>
                </div>
              )}
              {data.Evidence?.length > 0 && (
                <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--fc-border-light)"}}>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--fc-text-secondary)",marginBottom:6}}>Summary</div>
                  {data.Evidence.map((e,i) => <div key={i} style={{fontSize:12,color:"var(--fc-text-muted)",marginBottom:4}}><strong>{i+1}.</strong> {e.Fact||e}</div>)}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div style={{fontSize:12,color:"var(--fc-text-subtle)",fontStyle:"italic"}}>No detailed data available.</div>
      )}
    </div>
  );
}

const COLORS = ["#2563EB","#16A34A","#D97706","#DC2626","#7C3AED","#06B6D4"];

export default function SimilarTab({ resourceSlug, userSlug, candidateName }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState([
    { name:"Skills",                type:"sk",  value:0, remove:0 },
    { name:"Certifications",        type:"crt", value:0, remove:0 },
    { name:"Education",             type:"edu", value:0, remove:0 },
    { name:"Management Experience", type:"ml",  value:0, remove:0 },
    { name:"Industries",            type:"in",  value:0, remove:0 },
    { name:"Languages",             type:"lng", value:0, remove:0 },
  ]);
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [expandedKey, setExpandedKey] = useState(null);

  const scoreMap = (fs) => ({
    j_score:0, ex_score:0,
    e_score:  fs.find(f=>f.type==="edu")?.value||0,
    s_score:  fs.find(f=>f.type==="sk")?.value||0,
    i_score:  fs.find(f=>f.type==="in")?.value||0,
    l_score:  fs.find(f=>f.type==="lng")?.value||0,
    m_score:  fs.find(f=>f.type==="ml")?.value||0,
    c_score:  fs.find(f=>f.type==="crt")?.value||0,
  });

  const fetchData = (fs = filters) => {
    setLoading(true); setError("");
    // u_slug = logged-in user slug (auth), r_slug = resource being viewed
    if (!userSlug || !resourceSlug) { setError("Missing parameters"); setLoading(false); return; }
    post("get-similar-candidate-v2", { u_slug: userSlug, r_slug: resourceSlug, ...scoreMap(fs) })
      .then(d => {
        if (d && Number(d.status) === 1 && Array.isArray(d.matches)) {
          setItems(d.matches);
          if (d.filters_has_data) {
            const fd = d.filters_has_data;
            setFilters(prev => prev.map(f => {
              const map = { sk:"Skills", crt:"Certifications", edu:"Education", ml:"ManagementLevel", in:"Industries", lng:"Languages" };
              const key = map[f.type];
              const val = fd[key] != null ? parseInt(fd[key]*100) : f.value;
              return { ...f, value: val, remove: (!fd[key+"HasData"] && val===0) ? 1 : 0 };
            }));
          }
        } else {
          setError(d?.message || "No similar candidates found.");
        }
        setLoading(false);
      });
  };

  useEffect(() => { fetchData(); }, [resourceSlug]);

  const updateFilter = (type, newVal) => {
    setFilters(prev => {
      const idx = prev.findIndex(f=>f.type===type);
      const diff = newVal - parseInt(prev[idx].value);
      const others = prev.filter((_,i)=>i!==idx&&!prev[i].remove);
      let updated = [...prev];
      updated[idx] = {...updated[idx], value: newVal};
      others.forEach(f => {
        const i = prev.findIndex(x=>x.type===f.type);
        updated[i] = {...updated[i], value: Math.max(0, parseInt(updated[i].value) - Math.round(diff/others.length))};
      });
      return updated;
    });
  };

  const visibleFilters = filters.filter(f=>!f.remove);

  return (
    <SectionCard title="Similar Teammates">
      {!loading && items.length > 0 && visibleFilters.length > 0 && (
        <div style={{background:"var(--fc-bg-light)",border:"1px solid var(--fc-border)",borderRadius:12,padding:"16px 18px",marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"var(--fc-text-primary)"}}>Adjust Match Importance</div>
              <div style={{fontSize:11,color:"var(--fc-text-subtle)"}}>Slide to weight each category</div>
            </div>
            <button onClick={()=>fetchData()} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
              <i className="fa-solid fa-rotate-right me-1" style={{fontSize:10}}/>Update
            </button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:"10px 20px"}}>
            {visibleFilters.map(f => (
              <div key={f.type}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--fc-text-muted)",marginBottom:4}}>
                  <span style={{fontWeight:600}}>{f.name}</span>
                  <span style={{fontWeight:700,color:"#2563EB"}}>{f.value}%</span>
                </div>
                <input type="range" min={0} max={100} step={1} value={f.value}
                  onChange={e=>updateFilter(f.type,parseInt(e.target.value))}
                  style={{width:"100%",accentColor:"#2563EB"}} />
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{textAlign:"center",color:"var(--fc-text-subtle)",padding:"32px 0"}}><i className="fa-solid fa-spinner fa-spin" style={{fontSize:20}}/></div>
      ) : error || items.length===0 ? (
        <div style={{textAlign:"center",color:"var(--fc-text-subtle)",fontSize:13,padding:"32px 0"}}>{error||"No similar candidates found."}</div>
      ) : (
        <div style={{border:"1px solid var(--fc-border)",borderRadius:12,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"60px 2fr 1.5fr 1.2fr 68px 120px",padding:"9px 16px",background:"var(--fc-bg-light)",borderBottom:"1.5px solid var(--fc-border)",gap:8}}>
            {[["Score","center"],["Candidate","left"],["Role","left"],["Location","left"],["Avail","center"],["Actions","right"]].map(([h,a]) => (
              <div key={h} style={{fontSize:10,fontWeight:700,color:"var(--fc-text-subtle)",textTransform:"uppercase",letterSpacing:"0.08em",textAlign:a}}>{h}</div>
            ))}
          </div>

          {items.map((item,i) => {
            if(!item?.profile) return null;
            const p = item.profile;
            const name = p.r_name||p.profile_name||"Unknown";
            const loc  = [p.city_name,p.state_name,p.country_name].filter(Boolean).join(", ");

            const color    = COLORS[i%COLORS.length];
            const isOpen   = expandedKey===i;

            return (
              <div key={i} style={{borderBottom:i<items.length-1?"1px solid var(--fc-border-light)":"none",borderLeft:isOpen?"3px solid #2563EB":"3px solid transparent"}}>
                <div style={{display:"grid",gridTemplateColumns:"60px 2fr 1.5fr 1.2fr 68px 120px",padding:"12px 16px",alignItems:"center",gap:8,transition:"background 0.1s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--fc-bg-light)"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>

                  <div style={{textAlign:"center"}}>
                    <span style={{fontSize:13,fontWeight:800,padding:"3px 8px",borderRadius:7,background:"var(--fc-primary-light)",color:"#2563EB"}}>{item.score}</span>
                  </div>

                  <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                    <div style={{width:36,height:36,borderRadius:9,background:"var(--fc-border-light)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden",border:"1px solid var(--fc-border)"}}>
                      {p.profile_image_path
                        ? <img src={PROFILE_IMG_BASE+p.profile_image_path} alt={name} style={{width:"100%",height:"100%",objectFit:"cover"}} onError={e=>{e.target.style.display="none";e.target.nextSibling.style.display="flex";}} />
                        : null}
                      <i className="fa-solid fa-user" style={{fontSize:14,color:"var(--fc-text-subtle)",display:p.profile_image_path?"none":"flex"}} />
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:"var(--fc-text-primary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</div>
                      {p.user_email && <div style={{fontSize:11,color:"var(--fc-text-subtle)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.user_email}</div>}
                    </div>
                  </div>

                  <div style={{fontSize:12,color:"var(--fc-text-secondary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={p.user_profile_role}>
                    {p.user_profile_role||"—"}
                  </div>

                  <div style={{fontSize:12,color:"var(--fc-text-muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{loc||"—"}</div>

                  <div style={{display:"flex",justifyContent:"center"}}>
                    <AvailRing value={p.availability} />
                  </div>

                  <div style={{display:"flex",gap:5,justifyContent:"flex-end"}}>
                    <button onClick={()=>navigate(`/resources/details/${p.user_slug}`)}
                      style={{height:28,padding:"0 10px",borderRadius:7,border:"1px solid var(--fc-border)",background:"var(--fc-card-bg)",color:"var(--fc-text-secondary)",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}
                      onMouseEnter={e=>{e.currentTarget.style.background="var(--fc-primary-light)";e.currentTarget.style.borderColor="var(--fc-primary-border)";e.currentTarget.style.color="#2563EB";}}
                      onMouseLeave={e=>{e.currentTarget.style.background="var(--fc-card-bg)";e.currentTarget.style.borderColor="var(--fc-border)";e.currentTarget.style.color="var(--fc-text-secondary)";}}>
                      View
                    </button>
                    <button onClick={()=>setExpandedKey(isOpen?null:i)}
                      style={{height:28,width:28,borderRadius:7,border:"1px solid "+(isOpen?"var(--fc-primary-border)":"var(--fc-border)"),background:isOpen?"var(--fc-primary-light)":"var(--fc-card-bg)",color:isOpen?"#2563EB":"var(--fc-text-muted)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
                      <i className={`fa-solid fa-chevron-${isOpen?"up":"down"}`} style={{fontSize:9}}/>
                    </button>
                  </div>
                </div>
                {isOpen && <ScoreBreakdown item={item} candidateName={candidateName} />}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
